import { Storage } from 'aws-amplify';
import uniqid from 'uniqid';
import awsconfig from '../../../aws-exports';
import { getEnvAndRegion, getPublicImagePath } from '../../../components/inputComponents/FileUpload/fileUploadHelpers';

export type EmailImageField = 'headerImage' | 'emailIcon';

export const IMAGE_TOOLTIP = 'The file name will be used as the image name. Please use meaningful file names.\nRecommended image resolution: 567 × 567 px.\nAllowed image formats: PNG, JPG, SVG';
export const INVALID_IMAGE_MESSAGE = 'Invalid image format or resolution. Allowed formats are PNG, JPG, and SVG, and the recommended resolution is 567 × 567 px.';

const storageOptions = { level: 'public' as const, customPrefix: { public: '' } };
const savedImageKey = (field: EmailImageField) => `emailTemplates/ajo/${field}.json`;

const readFile = (file: Blob, asText = false): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    if (asText) reader.readAsText(file);
    else reader.readAsDataURL(file);
});

const imageContentType = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'png': return 'image/png';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'svg': return 'image/svg+xml';
        default: return undefined;
    }
};

export const validateEmailImage = async (file: File): Promise<boolean> => {
    const contentType = imageContentType(file);
    if (!contentType || (file.type && file.type !== contentType) || !file.size) return false;

    try {
        if (contentType === 'image/svg+xml') {
            const document = new DOMParser().parseFromString(await readFile(file, true), 'image/svg+xml');
            const svg = document.documentElement;
            if (document.querySelector('parsererror') || svg.localName !== 'svg' || svg.namespaceURI !== 'http://www.w3.org/2000/svg') return false;
        }

        const dataUrl = await readFile(new Blob([file], { type: contentType }));
        const data = dataUrl.slice(dataUrl.indexOf(',') + 1);
        if (contentType === 'image/png' && !data.startsWith('iVBORw0KGgo')) return false;
        if (contentType === 'image/jpeg' && !data.startsWith('/9j/')) return false;

        return await new Promise<boolean>((resolve) => {
            const image = new Image();
            // 567 × 567 px is a recommendation; other usable dimensions are allowed.
            image.onload = () => resolve(image.naturalWidth > 0 && image.naturalHeight > 0);
            image.onerror = () => resolve(false);
            image.src = dataUrl;
        });
    } catch {
        return false;
    }
};

export const loadEmailImage = async (field: EmailImageField): Promise<string> => {
    try {
        const result = await Storage.get(savedImageKey(field), {
            ...storageOptions,
            bucket: awsconfig.aws_user_files_s3_bucket,
            download: true,
            cacheControl: 'no-cache',
        });
        const saved = JSON.parse(await readFile(result.Body as Blob, true));
        if (typeof saved.location !== 'string' || !saved.location.startsWith('https://')) {
            throw new Error('Invalid saved email image location');
        }
        return saved.location;
    } catch (error) {
        const storageError = error as { code?: string; name?: string; $metadata?: { httpStatusCode?: number } };
        if (storageError?.code === 'NoSuchKey' || storageError?.name === 'NoSuchKey' || storageError?.$metadata?.httpStatusCode === 404) return '';
        throw error;
    }
};

export const uploadEmailImage = async (field: EmailImageField, file: File): Promise<string> => {
    if (!await validateEmailImage(file)) throw new Error(INVALID_IMAGE_MESSAGE);

    // A new directory preserves existing AJO references, including uploads with the same file name.
    const directory = `emailTemplates/${uniqid()}/${field}`;
    const key = `${directory}/${file.name}`;
    const publicBucket = awsconfig.aws_user_files_s3_bucket.replace('private', 'public');
    await Storage.put(key, file, {
        ...storageOptions,
        bucket: publicBucket,
        acl: 'public-read',
        contentType: imageContentType(file),
    });

    const { env, region, isValidRegion } = getEnvAndRegion(awsconfig.aws_user_files_s3_bucket);
    const location = `${getPublicImagePath(env, isValidRegion, region, directory)}/${encodeURIComponent(file.name)}`;
    // Save each field independently so updating one asset cannot overwrite the other field.
    try {
        await Storage.put(savedImageKey(field), JSON.stringify({ location }), {
            ...storageOptions,
            bucket: awsconfig.aws_user_files_s3_bucket,
            contentType: 'application/json',
            cacheControl: 'no-cache',
        });
    } catch (error) {
        try {
            // The save may have succeeded even if its response was lost.
            if (await loadEmailImage(field) === location) return location;

            const { httpStatusCode, attempts = 1 } = (error as {
                $metadata?: { httpStatusCode?: number; attempts?: number };
            }).$metadata || {};
            // Only roll back a definite rejection. Timeouts, server errors and
            // retried requests may still have committed a reference to this image.
            if (attempts === 1 && httpStatusCode >= 400 && httpStatusCode < 500 && httpStatusCode !== 408 && httpStatusCode !== 429) {
                await Storage.remove(key, { ...storageOptions, bucket: publicBucket });
            } else {
                console.warn('Email image retained because its metadata save outcome is uncertain.', { bucket: publicBucket, key });
            }
        } catch (cleanupError) {
            console.warn('Could not verify or clean up the new email image.', { bucket: publicBucket, key }, cleanupError);
        }
        throw error;
    }
    return location;
};
